/**
 * Google Analytics 4 Event Tracking
 */

import { getWorkerGeoAnalyticsProps } from "@/lib/serverTemplateDetection";
import { getCanonicalLocation, regionFromCountry } from "@/lib/canonicalLocation";

// Amplitude import removed

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const GA_MEASUREMENT_ID =
  import.meta.env.VITE_GA_MEASUREMENT_ID ||
  import.meta.env.VITE_GA_TRACKING_ID ||
  "G-D1JQVWYJM6";
// Amplitude constants removed

/**
 * Track social media link clicks
 */
export function trackSocialClick(platform: string, linkUrl: string) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "social_click", {
    event_category: "Social Media",
    event_label: platform,
    link_url: linkUrl,
    platform: platform.toLowerCase(),
  });

  // Amplitude tracking completely removed
}

/**
 * Track photo interactions (clicks, views)
 */
export function trackPhotoInteraction(
  action: "click" | "view",
  photoId: string | number,
  photoTitle?: string,
  pagePath?: string
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "photo_interaction", {
    event_category: "Content",
    event_label: photoTitle || `Photo ${photoId}`,
    action: action,
    photo_id: String(photoId),
    page_path: pagePath || window.location.pathname,
  });

}

/** GA4 mirror for PostHog `base_template_ui_click` */
export function trackBaseTemplateUIGA(params: {
  templateName: string;
  elementId: string;
  label: string;
  surface: string;
  href?: string;
  extra?: Record<string, unknown>;
}) {
  if (typeof window.gtag !== "function") return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(
      ([, v]) => v !== undefined && v !== "",
    ),
  );

  window.gtag("event", "base_template_ui_click", {
    event_category: "BaseTemplate",
    template_name: params.templateName,
    event_label: params.label,
    element_id: params.elementId,
    surface: params.surface,
    link_url: params.href,
    page_path: window.location.pathname,
    ...geoFlat,
    ...params.extra,
  });
}

/** GA4 mirror for PostHog `base_template_hero_community_cta_click` */
export function trackBaseTemplateHeroCommunityCtaGA(params: {
  templateName: string;
  label: string;
  href?: string;
  extra?: Record<string, unknown>;
}) {
  if (typeof window.gtag !== "function") return;

  const geo = getWorkerGeoAnalyticsProps();
  const geoFlat = Object.fromEntries(
    Object.entries(geo).filter(
      ([, v]) => v !== undefined && v !== "",
    ),
  );

  window.gtag("event", "base_template_hero_community_cta_click", {
    event_category: "BaseTemplate",
    template_name: params.templateName,
    event_label: params.label,
    link_url: params.href,
    page_path: window.location.pathname,
    cta_kind: "community",
    ...geoFlat,
    ...params.extra,
  });
}

/**
 * Track video/reel interactions (clicks, plays)
 */
export function trackVideoInteraction(
  action: "click" | "play",
  videoId: string | number,
  videoTitle?: string,
  embedId?: string,
  pagePath?: string
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "video_interaction", {
    event_category: "Content",
    event_label: videoTitle || `Video ${videoId}`,
    action: action,
    video_id: String(videoId),
    embed_id: embedId,
    page_path: pagePath || window.location.pathname,
  });

}

/**
 * Track internal link clicks
 */
export function trackLinkClick(linkText: string, linkUrl: string) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "link_click", {
    event_category: "Navigation",
    event_label: linkText,
    link_url: linkUrl,
  });

}

/**
 * Track button clicks with enhanced Google Analytics tracking
 */
export function trackButtonClickGA(
  buttonText: string,
  buttonId: string,
  buttonType: string,
  service?: string,
  countryCode?: string,
  templateType?: string
) {
  if (typeof window.gtag !== "function") return;

  // VERDAD ÚNICA: país/región del Worker (canonicalLocation) — nada de
  // derivar país del idioma del browser ni leer la key legacy user_region.
  const canonical = getCanonicalLocation();
  const region =
    localStorage.getItem("user_region_manual") ||
    regionFromCountry(canonical.country) ||
    "LATAM";
  const timezone =
    typeof window !== "undefined"
      ? Intl.DateTimeFormat().resolvedOptions().timeZone
      : "";
  const detectedCountry = countryCode || canonical.country || undefined;

  // Enhanced GA4 event tracking with country information
  window.gtag("event", "button_click", {
    event_category: "Interaction",
    event_label: buttonText,
    button_id: buttonId,
    button_type: buttonType,
    service: service || "general",
    page_path: window.location.pathname,
    page_title: document.title,
    // Enhanced country/template tracking
    region: region,
    country_code: detectedCountry,
    template_type: templateType,
    timezone: timezone,
    // `browserLang` quedó colgado tras remover la derivación país-por-idioma (ver
    // comentario arriba). Solo es el campo `language` del evento GA — leemos
    // navigator.language directo para no lanzar ReferenceError en cada click.
    language: typeof navigator !== "undefined" ? navigator.language : "",
    is_biolink_user: templateType === "BioLinkTemplate",
  });

  // Track conversion events for payment buttons
  if (buttonType.includes("modal_open") || buttonType.includes("payment")) {
    window.gtag("event", "conversion_funnel", {
      event_category: "Conversion",
      event_label: `${buttonText} - Modal Open`,
      funnel_step: "modal_open",
      service: service || "general",
    });
  }

  // Track form submissions for Tally
  if (buttonType.includes("tally_form")) {
    window.gtag("event", "form_start", {
      event_category: "Form",
      event_label: buttonText,
      form_type: "tally",
      form_id: buttonId.includes("tally") ? "contact_form" : "unknown",
    });
  }

}

/**
 * Track payment modal interactions
 */
export function trackPaymentModalGA(
  action: "open" | "close" | "method_select",
  service: string,
  method?: string
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "payment_modal", {
    event_category: "Payment",
    event_label: `${service} - ${action}`,
    action: action,
    service: service,
    payment_method: method || "unknown",
    page_path: window.location.pathname,
  });

  // Track conversion funnel
  if (action === "method_select") {
    window.gtag("event", "conversion_funnel", {
      event_category: "Conversion",
      event_label: `${service} - Payment Method Selected`,
      funnel_step: "payment_method_selected",
      service: service,
      payment_method: method,
    });
  }

}

/**
 * Track Best Friend Instagram (Close Friends) click (membership subscription)
 * For conversion and revenue attribution
 */
export function trackBestFriendInstagramClickGA() {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "begin_checkout", {
    event_category: "Conversion",
    event_label: "Best Friend Instagram (Close Friends)",
    value: 6999,
    currency: "ARS",
    content_type: "bestfriend_instagram",
    page_path: window.location.pathname,
  });
}

/**
 * Track template/layout usage
 */
export function trackTemplateUsageGA(
  template: string,
  region: string,
  features: string[]
) {
  if (typeof window.gtag !== "function") return;

  window.gtag("event", "template_usage", {
    event_category: "Template",
    event_label: `${template} - ${region}`,
    template: template,
    region: region,
    features: features.join(","),
    page_path: window.location.pathname,
  });

}
